import type { ReactElement, RefObject, SetStateAction, Ref } from 'react';
import { useCallback, useImperativeHandle, useRef } from 'react';
import classNames from 'classnames';
import Icon from 'components/atoms/icon/Icon';
import { useTransitionController } from 'util/react-transition-component';
import { useChangeTransitionState } from 'util/react-transition-component/hooks/useChangeTransitionState';
import { renderCta } from '../../../util/cta/cta.template';
import styles from './SiteHeader.module.scss';
import { setupTransitionInTimeline, setupTransitionOutTimeline } from './SiteHeader.transitions';
import type { SiteHeaderProps, SiteHeaderRef } from './SiteHeader.data';
import NavigationList from './navigation-list/NavigationList';
import HamburgerButton from './hamburger-button/HamburgerButton';
import useStickyNav from './hooks/useStickyNav';
import { useMobileViewport } from '../../../hooks/useMobileViewport';
import sharedVariables from '../../../data/shared-variable/shared-variables.json';
import { StorySlug } from '../../../util/storyblok/types/api.types';
import { useRouter } from 'next/router';
import { useGlobalStore } from '@/stores/global';

export default function SiteHeader({
  isVisible,
  ref,
}: SiteHeaderProps & { ref?: Ref<SiteHeaderRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = ((ref as RefObject<SiteHeaderRef | null>).current?.elementRef ||
    localRef) as RefObject<HTMLElement | null>;
  const navigationListRef = useRef(null);
  const globalStore = useGlobalStore();

  const transitionController = useTransitionController(
    () => ({
      ref: elementRef,
      refs: {
        elementRef,
        navigationListRef,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
    [],
  );
  const isMobileViewport = useMobileViewport(sharedVariables.deviceState.MIN_768);
  const { setOffsetY } = useStickyNav(elementRef, transitionController);
  useChangeTransitionState(transitionController, isVisible);

  const setPageScrollOffsetY = useCallback(
    (yOffset: SetStateAction<number>) => {
      if (!isMobileViewport) {
        setOffsetY(yOffset);
      }
    },
    [setOffsetY, isMobileViewport],
  );

  useImperativeHandle(
    ref,
    () => ({
      setPageScrollOffsetY,
      elementRef,
    }),
    [setPageScrollOffsetY, elementRef],
  );

  const router = useRouter();
  const onSkipToMainContent = (e: { preventDefault: () => void }) => {
    e.preventDefault();

    const mainContent: HTMLElement | null = document.querySelector('#main-content');
    if (mainContent) {
      mainContent.setAttribute('tabindex', '-1');
      mainContent.focus();
      mainContent.removeAttribute('tabindex');
    }
  };

  return (
    <nav
      ref={elementRef}
      className={classNames(styles.siteHeader, 'grid-gutter', {
        [styles.noMixBlendMode]: router.asPath.includes(`/${StorySlug.Cases}/`),
      })}
      aria-label={globalStore?.globalSettings?.navigation_alt}
    >
      {renderCta({
        innerTemplate: <Icon name="logo" className={classNames(styles.icon)} />,
        props: {
          link: {
            href: '/',
          },
          'aria-label': globalStore?.globalSettings?.logo_alt,
          className: classNames(styles.logo),
        },
      })}
      <a href="#main-content" onClick={onSkipToMainContent} className="skip-link">
        {globalStore?.globalSettings?.skip_main_content}
      </a>
      <NavigationList ref={navigationListRef} className={classNames(styles.list)} />
      <HamburgerButton className={classNames(styles.hamburgerButton)} />
    </nav>
  );
}
